// RiskRegisterPage — risk list for the selected register.
// Rendered in the right panel of RiskLayout.
// Route: /t/:slug/risks (index route)

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useSelectedRegister } from './RiskLayout'
import { risksApi, lifecycleStateLabels, lifecycleStateColors, type Risk } from '@/lib/api/risks'
import { scoringApi, type LikelihoodLevel } from '@/lib/api/scoring'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CreateRiskDialog } from './CreateRiskDialog'
import { Plus, Search, AlertTriangle, Clock, ChevronRight, X, GitBranch, CornerLeftUp } from 'lucide-react'
import { SortableHeader, useSortState, type SortState } from '@/components/shared/SortableHeader'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// --- Sort column type ---

type SortColumn = 'name' | 'owner' | 'likelihood' | 'impact' | 'score' | 'appetite' | 'status'

// --- Score computation helper ---

function computeRiskScore(risk: Risk, likelihoodMap: Record<string, LikelihoodLevel>): number | null {
  const likelihoodLevel = risk.residual_likelihood_level_id
    ? likelihoodMap[risk.residual_likelihood_level_id]
    : null
  if (!likelihoodLevel) return null

  const residualImpacts = (risk.impact_assessments ?? [])
    .filter((a) => a.score_type === 'residual')
    .map((a) => a.level_number)
  if (residualImpacts.length === 0) return null

  return likelihoodLevel.level_number * Math.max(...residualImpacts)
}

// --- Sort comparator ---

function sortRisks(
  risks: Risk[],
  sort: SortState<SortColumn>,
  likelihoodMap: Record<string, LikelihoodLevel>,
): Risk[] {
  if (!sort.column) return risks

  const sorted = [...risks].sort((a, b) => {
    let cmp = 0
    switch (sort.column) {
      case 'name':
        cmp = a.name.localeCompare(b.name)
        break
      case 'owner':
        cmp = (a.owner_display_name ?? '').localeCompare(b.owner_display_name ?? '')
        break
      case 'likelihood': {
        const aL = a.residual_likelihood_level_id ? (likelihoodMap[a.residual_likelihood_level_id]?.level_number ?? 0) : 0
        const bL = b.residual_likelihood_level_id ? (likelihoodMap[b.residual_likelihood_level_id]?.level_number ?? 0) : 0
        cmp = aL - bL
        break
      }
      case 'impact': {
        const aMax = Math.max(0, ...(a.impact_assessments ?? []).filter((i) => i.score_type === 'residual').map((i) => i.level_number))
        const bMax = Math.max(0, ...(b.impact_assessments ?? []).filter((i) => i.score_type === 'residual').map((i) => i.level_number))
        cmp = aMax - bMax
        break
      }
      case 'score': {
        const aS = computeRiskScore(a, likelihoodMap) ?? 0
        const bS = computeRiskScore(b, likelihoodMap) ?? 0
        cmp = aS - bS
        break
      }
      case 'appetite': {
        const aA = a.outside_appetite === true ? 1 : 0
        const bA = b.outside_appetite === true ? 1 : 0
        cmp = aA - bA
        break
      }
      case 'status': {
        const order = ['draft', 'identify', 'assess', 'manage', 'closed']
        cmp = order.indexOf(a.lifecycle_state) - order.indexOf(b.lifecycle_state)
        break
      }
    }
    return sort.direction === 'desc' ? -cmp : cmp
  })
  return sorted
}

export function RiskRegisterPage() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const { registerId, registerName } = useSelectedRegister()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  function handleRiskClick(riskId: string) {
    navigate({ to: `/t/${slug}/risks/${riskId}` })
  }

  if (!registerId) {
    return <EmptySelection />
  }

  return (
    <>
      <RiskListPanel
        registerId={registerId}
        registerName={registerName}
        onRiskClick={handleRiskClick}
        onCreateClick={() => setCreateDialogOpen(true)}
        slug={slug}
      />

      <CreateRiskDialog
        registerId={registerId}
        registerName={registerName}
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(riskId) => {
          setCreateDialogOpen(false)
          handleRiskClick(riskId)
        }}
      />
    </>
  )
}

// --- Empty state when no register is selected ---

function EmptySelection() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <div className="space-y-2 text-center">
        <ChevronRight className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm">Select a register from the tree to view its risks.</p>
      </div>
    </div>
  )
}

// --- Risk list panel ---

interface RiskListPanelProps {
  registerId: string
  registerName: string
  onRiskClick: (riskId: string) => void
  onCreateClick: () => void
  slug: string
}

function RiskListPanel({
  registerId,
  registerName,
  onRiskClick,
  onCreateClick,
  slug,
}: RiskListPanelProps) {
  const api = useApi()
  const riskApi = risksApi(api)
  const scoring = scoringApi(api)

  // Filter state
  const [nameFilter, setNameFilter] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')

  // Sort state
  const [sort, , handleSort] = useSortState<SortColumn>()

  // Server-side search uses name filter
  const serverFilter = nameFilter ? { search: nameFilter } : undefined

  const {
    data: risks,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...queryKeys.registers.detail(slug, registerId), 'risks', serverFilter],
    queryFn: () => riskApi.list(registerId, serverFilter),
  })

  // Fetch likelihood levels for resolving level names
  const { data: likelihood } = useQuery({
    queryKey: queryKeys.scoring.likelihoodLevels(slug),
    queryFn: () => scoring.getLikelihood(),
  })

  // Build lookup: levelId -> level
  const likelihoodMap = useMemo(() => {
    const map: Record<string, LikelihoodLevel> = {}
    for (const l of likelihood?.levels ?? []) {
      map[l.id] = l
    }
    return map
  }, [likelihood])

  // Distinct owners for filter dropdown
  const distinctOwners = useMemo(() => {
    if (!risks) return []
    const owners = new Set<string>()
    for (const r of risks) {
      if (r.owner_display_name) owners.add(r.owner_display_name)
    }
    return [...owners].sort()
  }, [risks])

  // Client-side owner filtering + sorting
  const displayRisks = useMemo(() => {
    if (!risks) return []
    let filtered = risks
    if (ownerFilter) {
      filtered = filtered.filter((r) => r.owner_display_name === ownerFilter)
    }
    return sortRisks(filtered, sort, likelihoodMap)
  }, [risks, ownerFilter, sort, likelihoodMap])

  const hasActiveFilters = nameFilter !== '' || ownerFilter !== ''

  const clearFilters = useCallback(() => {
    setNameFilter('')
    setOwnerFilter('')
  }, [])

  return (
    <>
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 border-b p-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold">{registerName}</h1>
          <p className="text-xs text-muted-foreground">
            {risks
              ? `${displayRisks.length}${displayRisks.length !== risks.length ? ` of ${risks.length}` : ''} risk${risks.length !== 1 ? 's' : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button size="sm" onClick={onCreateClick}>
            <Plus className="mr-1 h-4 w-4" />
            New Risk
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 border-b bg-muted/10 px-4 py-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by name..."
            className="h-8 pl-8 text-sm"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>
        <select
          className="h-8 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value)}
        >
          <option value="">All owners</option>
          {distinctOwners.map((owner) => (
            <option key={owner} value={owner}>
              {owner}
            </option>
          ))}
        </select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={clearFilters}>
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Column headers */}
      {risks && risks.length > 0 && (
        <TooltipProvider delayDuration={300}>
        <div className="flex items-center border-b bg-muted/30 px-4 py-1.5 text-xs font-medium text-muted-foreground">
          <SortableHeader
            label="Risk"
            column="name"
            sort={sort}
            onSort={handleSort}
            className="w-[25%] min-w-0"
          />
          <SortableHeader
            label="Appetite"
            column="appetite"
            sort={sort}
            onSort={handleSort}
            className="w-[5%] justify-center"
          />
          <SortableHeader
            label="Likelihood"
            column="likelihood"
            sort={sort}
            onSort={handleSort}
            className="w-[14%] justify-center"
          />
          <SortableHeader
            label="Impact"
            column="impact"
            sort={sort}
            onSort={handleSort}
            className="w-[18%]"
          />
          <SortableHeader
            label="Score"
            column="score"
            sort={sort}
            onSort={handleSort}
            className="w-[10%] justify-center"
          />
          <span className="w-[10%] text-center">Flags</span>
          <SortableHeader
            label="Status"
            column="status"
            sort={sort}
            onSort={handleSort}
            className="w-20 justify-center"
          />
          <span className="w-20 text-right">Code</span>
        </div>
        </TooltipProvider>
      )}

      {/* Risk list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <TableSkeleton rows={6} />}

        {error && (
          <div className="p-4">
            <ErrorState
              message="Failed to load risks"
              technicalDetail={error.message}
              onRetry={() => refetch()}
            />
          </div>
        )}

        {risks && displayRisks.length === 0 && (
          <div className="flex h-48 items-center justify-center text-muted-foreground">
            <p className="text-sm">
              {hasActiveFilters ? 'No risks match your filters.' : 'No risks in this register.'}
            </p>
          </div>
        )}

        {displayRisks.length > 0 && (
          <div className="divide-y">
            {displayRisks.map((risk) => (
              <RiskListRow
                key={risk.id}
                risk={risk}
                likelihoodMap={likelihoodMap}
                onClick={() => onRiskClick(risk.id)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// --- Individual risk row ---

interface RiskListRowProps {
  risk: Risk
  likelihoodMap: Record<string, LikelihoodLevel>
  onClick: () => void
}

function RiskListRow({ risk, likelihoodMap, onClick }: RiskListRowProps) {
  const residualLevel = risk.residual_likelihood_level_id
    ? (likelihoodMap[risk.residual_likelihood_level_id] ?? null)
    : null

  // Build sorted residual impacts from backend-resolved data, max first
  const residualImpacts = useMemo(() => {
    const impacts = (risk.impact_assessments ?? [])
      .filter((a) => a.score_type === 'residual')
      .map((a) => ({
        tableName: a.impact_table_name,
        levelNumber: a.level_number,
      }))
      .sort((a, b) => b.levelNumber - a.levelNumber)
    return impacts
  }, [risk.impact_assessments])

  const maxImpactLevel = residualImpacts[0]?.levelNumber ?? 0

  // Compute score in browser: likelihood × max residual impact
  const score = useMemo(() => computeRiskScore(risk, likelihoodMap), [risk, likelihoodMap])

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="flex cursor-pointer items-center px-4 py-3 transition-colors hover:bg-accent/30"
        onClick={onClick}
      >
        {/* Risk name and metadata — 25% */}
        <div className="w-[25%] min-w-0">
          <div className="mb-0.5 truncate text-sm font-medium">{risk.name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="truncate">{risk.taxonomy_node_name}</span>
            {risk.owner_display_name && (
              <>
                <span>·</span>
                <span className="truncate">{risk.owner_display_name}</span>
              </>
            )}
          </div>
        </div>

        {/* Appetite flag — 5% */}
        <div className="flex w-[5%] justify-center">
          {risk.outside_appetite === true && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Residual risk score is outside appetite</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Likelihood — 14% */}
        <div
          className="w-[14%] text-center text-xs tabular-nums"
          title={residualLevel ? `Likelihood: ${residualLevel.label}` : 'No likelihood set'}
        >
          {residualLevel ? (
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
              {residualLevel.level_number}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Impact — 18%, stacked, max at top in bold */}
        <div className="w-[18%] text-xs">
          {residualImpacts.length > 0 ? (
            <div className="space-y-0.5">
              {residualImpacts.map((impact) => (
                <div
                  key={impact.tableName}
                  className={cn(
                    'flex items-center gap-1.5 tabular-nums',
                    impact.levelNumber === maxImpactLevel && maxImpactLevel > 0
                      ? 'font-bold text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block w-5 rounded px-1 text-center',
                      impact.levelNumber === maxImpactLevel && maxImpactLevel > 0
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-muted',
                    )}
                  >
                    {impact.levelNumber}
                  </span>
                  <span className="truncate">{impact.tableName}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Score — 10% */}
        <div className="w-[10%] text-center text-xs tabular-nums">
          {score !== null ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-800">
              {score}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Flags — 10% (stale taxonomy, overdue assessments) */}
        <div className="flex w-[10%] items-center justify-center gap-2">
          {risk.taxonomy_version_stale && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Clock className="h-4 w-4 text-amber-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Taxonomy has been updated since this risk was classified</p>
              </TooltipContent>
            </Tooltip>
          )}
          {risk.has_overdue_assessments && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Has overdue assessments</p>
              </TooltipContent>
            </Tooltip>
          )}
          {risk.child_risk_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5 text-muted-foreground">
                  <GitBranch className="h-3.5 w-3.5" />
                  <span className="text-xs tabular-nums">{risk.child_risk_count}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{risk.child_risk_count} child risk{risk.child_risk_count !== 1 ? 's' : ''}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {risk.parent_risk_id && (
            <Tooltip>
              <TooltipTrigger asChild>
                <CornerLeftUp className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Child of another risk</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Status */}
        <div className="flex w-20 justify-center">
          <Badge
            variant="outline"
            className={cn('px-1.5 py-0 text-xs', lifecycleStateColors[risk.lifecycle_state])}
          >
            {lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
          </Badge>
        </div>

        {/* Short code — rightmost */}
        <div className="w-20 text-right">
          {risk.short_code ? (
            <span className="font-mono text-xs text-muted-foreground">{risk.short_code}</span>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
